import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { automacaoService } from '../services/api';
import '../styles/AutomacaoValidacaoOk.css';

const formatTimer = (segundos) => {
  const s = Math.max(0, segundos || 0);
  const min = Math.floor(s / 60);
  const sec = s % 60;
  return `${min}:${String(sec).padStart(2, '0')}`;
};

const AutomationOverlay = () => {
  const { user } = useAuth();
  const [estado, setEstado] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const isBkoRole = user && ['backoffice', 'supervisor_bko'].includes(user.role);

  useEffect(() => {
    if (!isBkoRole) return undefined;

    const poll = async () => {
      try {
        const data = await automacaoService.getEstadoAtual();
        setEstado(data);
      } catch (e) {
        /* silencioso */
      }
    };

    poll();
    const interval = setInterval(poll, 2000);
    return () => clearInterval(interval);
  }, [isBkoRole]);

  const handleResponder = async (trabalhando) => {
    try {
      setEnviando(true);
      const data = await automacaoService.responderConfirmacaoBko(trabalhando);
      setEstado(data);
    } catch (e) {
      console.error(e);
    } finally {
      setEnviando(false);
    }
  };

  if (!isBkoRole) return null;

  const confirmacao = estado?.confirmacao_bko;
  const mostrarConfirmacao = confirmacao?.ativa && !estado?.em_execucao && !confirmacao?.minha_resposta;

  if (mostrarConfirmacao) {
    return (
      <div className="automacao-overlay automacao-overlay--confirmacao" role="alertdialog" aria-live="polite">
        <h2>Automação NEO — {confirmacao.slot}</h2>
        <p className="automacao-overlay-lead">
          O robô está programado para rodar agora. Antes de bloquear o CRM, precisamos saber:
        </p>
        <p className="automacao-overlay-pergunta">Você está trabalhando no backoffice neste momento?</p>
        <p className="automacao-overlay-hint">
          Se alguém do BKO informar que está trabalhando, a automação das {confirmacao.slot} será suspensa.
          Se todos responderem que não estão trabalhando, o robô inicia na hora.
          Sem resposta em 3 minutos, o robô inicia automaticamente.
        </p>
        <p className="automacao-overlay-timer">
          Início automático em <strong>{formatTimer(confirmacao.segundos_restantes)}</strong>
        </p>
        <div className="automacao-overlay-actions">
          <button
            type="button"
            className="btn-primary automacao-btn-trabalhando"
            disabled={enviando}
            onClick={() => handleResponder(true)}
          >
            Sim, estou trabalhando
          </button>
          <button
            type="button"
            className="btn-secondary automacao-btn-livre"
            disabled={enviando}
            onClick={() => handleResponder(false)}
          >
            Não, pode iniciar
          </button>
        </div>
      </div>
    );
  }

  if (!estado?.em_execucao) return null;

  const run = estado.run;
  const processadas = (run?.qtd_sucesso || 0) + (run?.qtd_erro || 0);
  const total = run?.qtd_total || 0;
  const progressoPct = total > 0 ? Math.round((processadas / total) * 100) : 0;

  return (
    <div className="automacao-overlay" role="alert" aria-live="polite">
      <div className="automacao-overlay-spinner" aria-hidden />
      <h2>Automação NEO em execução</h2>
      <p>O backoffice está temporariamente bloqueado enquanto o robô cadastra vendas no NEO SALES.</p>
      {run && (
        <div className="automacao-overlay-details">
          <p className="automacao-overlay-run">Run #{run.id}</p>
          <p className="automacao-overlay-step">{run.mensagem_atual}</p>
          {(run.venda_atual_id || run.venda_atual_cpf) && (
            <p className="automacao-overlay-venda">
              {run.venda_atual_id && <>Venda <strong>#{run.venda_atual_id}</strong></>}
              {run.venda_atual_cpf && <> · CPF {run.venda_atual_cpf}</>}
            </p>
          )}
          {run.venda_atual_pacote && (
            <p className="automacao-overlay-pacote">{run.venda_atual_pacote}</p>
          )}
          <div className="automacao-overlay-progress" aria-hidden>
            <div className="automacao-overlay-progress-bar" style={{ width: `${progressoPct}%` }} />
          </div>
          <p className="automacao-overlay-stats">
            Processadas: {processadas}/{total} · Sucesso: {run.qtd_sucesso || 0} · Erro: {run.qtd_erro || 0}
          </p>
          <p className="automacao-overlay-hint">Aguarde o término para voltar a usar o CRM.</p>
        </div>
      )}
    </div>
  );
};

export default AutomationOverlay;
